import PageMeta from "../components/PageMeta";
import ListingResultsView from "../components/ListingResultsView";
import ListingSort, { normalizeSort } from "../components/ListingSort";
import uiI18n from "../i18n";
import React, { useState, useEffect, useRef } from "react";
import SaveSearchPrompt from "../components/SaveSearchPrompt";
import ListBiens from "../components/ListBiens";

import Spinner from '../components/Spinner';
import SearchForm from './SearchForm';
import citiesAndDelegations from '../data/citiesAndDelegations.js';
import { canonicalDelegation } from '../data/delegationAliases';
import { useTranslation } from "react-i18next";
import { getOffers } from '../Services/biensService';
import { useLocation } from "react-router-dom";

export default function Offers() {
  // ?view=map opens the results directly on the map (mobile "Voir sur la carte").
  const [resultsView, setResultsView] = useState(() => new URLSearchParams(window.location.search).get("view") === "map" ? "map" : "list");
  const [offerListings, setOfferListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState();
  // Offered after each search, saved only if the visitor accepts (SaveSearchPrompt).
  const [searchOffer, setSearchOffer] = useState(null);
  const [hasMore, setHasMore] = useState(true); 
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestVersion = useRef(0);
  const moreRequest = useRef(null);
  const [selectedDelegation, setSelectedDelegation] = useState('');
  const [category, setCategory] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [libelle, setLibelle] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchTrigger, setSearchTrigger] = useState(false);
  const [rooms, setRooms] = useState([]); // Gestion des pièces
  const location = useLocation();
  const { t } = useTranslation();



  const handleSearch = (searchFilters) => {
    const query = new URLSearchParams();
    const sort = normalizeSort(new URLSearchParams(window.location.search).get('sort'), false, searchFilters.category);
    if (sort !== 'recent') query.set('sort', sort);
    if (searchFilters.selectedCity) query.append('city', searchFilters.selectedCity);
    if (searchFilters.selectedDelegation) query.append('delegation', searchFilters.selectedDelegation);
    if (searchFilters.minPrice) query.append('minPrice', searchFilters.minPrice);
    if (searchFilters.maxPrice) query.append('maxPrice', searchFilters.maxPrice);
    if (searchFilters.category) query.append('category', searchFilters.category);
    if (searchFilters.libelle) query.append('libelle', searchFilters.libelle);
    if (searchFilters.rooms && searchFilters.rooms.length > 0) {
      query.append('rooms', searchFilters.rooms.join(',')); // Ajouter les pièces aux filtres de recherche
    }

    window.history.pushState(null, '', `?${query.toString()}`);

    setSelectedCity(searchFilters.selectedCity);
    setSelectedDelegation(searchFilters.selectedDelegation);
    setMinPrice(searchFilters.minPrice);
    setMaxPrice(searchFilters.maxPrice);
    setCategory(searchFilters.category);
    setLibelle(searchFilters.libelle);
    setRooms(searchFilters.rooms); // Mettre à jour les pièces

    setSearchTrigger(prev => !prev);
  };

  const handleReset = () => {
    window.history.pushState(null, '', window.location.pathname);

    setSelectedCity('');
    setSelectedDelegation('');
    setMinPrice('');
    setMaxPrice('');
    setCategory('');
    setLibelle('');
    setRooms([]); // Réinitialiser les pièces

    setSearchTrigger(prev => !prev);
  };

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    // Search aliases in links (e.g. delegation=Zaouia) become the catalog name in the URL itself.
    const delegation = canonicalDelegation(query.get('city'), query.get('delegation'));
    if (delegation && delegation !== query.get('delegation')) {
      query.set('delegation', delegation);
      window.history.replaceState(window.history.state, '', '?' + query.toString());
    }
    setSelectedCity(query.get('city') || '');
    setSelectedDelegation(query.get('delegation') || '');
    setMinPrice(query.get('minPrice') || '');
    setMaxPrice(query.get('maxPrice') || '');
    setCategory(query.get('category') || '');
    setLibelle(query.get('libelle') || '');
    setRooms(query.get('rooms') ? query.get('rooms').split(',') : []); // Charger les pièces depuis l'URL

    const version = ++requestVersion.current;
    moreRequest.current = null;
    setLoadingMore(false);
    setHasMore(false);
    setPage(0);
    setOfferListings([]);
    setLoading(true);
    const applied = Object.fromEntries(query.entries());
    applied.rooms = query.get('rooms') ? query.get('rooms').split(',') : [];
    let active = true;
    setError(null);
    getOffers(0, applied).then(data => {
      if (!active) return;
      setOfferListings(data);
      setHasMore(data.length === 20);
      setSearchOffer({ ...applied, kind: 'bien', offersOnly: true });
    }).catch(error => { if (active) setError(error.message); })
      .finally(() => { if (active) setLoading(false); });

  return () => { active = false; if (requestVersion.current === version) requestVersion.current++; };
  }, [location.search, searchTrigger]);

  const onFetchMoreListings = async () => {
    if (loading || moreRequest.current !== null || !hasMore) return;
    const version = requestVersion.current;
    moreRequest.current = version;
    setLoadingMore(true);
    setError(null);
    try {
      const applied = Object.fromEntries(new URLSearchParams(window.location.search).entries());
      applied.rooms = applied.rooms ? applied.rooms.split(',') : [];
      const fetcher = getOffers;
      const result = await fetcher(page + 1, applied);
      if (version !== requestVersion.current) return;
      const additional = result;
      setOfferListings(previous => [...new Map([...previous, ...additional].map(item => [item.id ?? item.ID, item])).values()]);
      setHasMore(result.length === 20);
      setPage(page + 1);
    } catch (error) {
      if (version === requestVersion.current) setError(error.message);
    } finally {
      if (version === requestVersion.current) { moreRequest.current = null; setLoadingMore(false); }
    }
  };


  const handleSort = value => {
    const next = new URLSearchParams(window.location.search);
    next.delete('page');
    if (value === 'recent') next.delete('sort'); else next.set('sort', value);
    window.history.pushState(null, '', '?' + next.toString());
    setSearchTrigger(previous => !previous);
  };
  return (
    <div className='max-w-7xl mx-auto px-3'>
      <PageMeta titleKey="seo.offers" path="/offers" />
      <h1 className='text-3xl text-center mt-6 font-bold mb-6'> {t('offer')}</h1>
      <SearchForm 
        citiesAndDelegations={citiesAndDelegations} 
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        selectedDelegation={selectedDelegation}
        setSelectedDelegation={setSelectedDelegation}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        category={category}
        setCategory={setCategory}
        libelle={libelle}
        setLibelle={setLibelle}
        rooms={rooms} // Passer rooms à SearchForm
        setRooms={setRooms} // Ajouter le setter pour rooms
        handleSearch={handleSearch}
        handleReset={handleReset}
      />
      <ListingSort category={new URLSearchParams(window.location.search).get('category') || ''} value={new URLSearchParams(window.location.search).get('sort')} onChange={handleSort} />

      {error && <p role="alert">{error}</p>}
      <SaveSearchPrompt search={searchOffer} />
      {loading ? (
        <Spinner />
      ) : offerListings && offerListings.length > 0 ? (
        <>
          <p className="text-[#457b9d] mb-2 font-semibold">
            {offerListings.length}{' '}{selectedDelegation || selectedCity
              ? uiI18n.t('ui.offers_city', { city: selectedDelegation
                ? uiI18n.t(`delegations.${selectedCity}.${selectedDelegation}`, selectedDelegation)
                : uiI18n.t(`cities.${selectedCity}`, selectedCity) })
              : uiI18n.t('ui.offers_found')}
          </p>
          <main>
            <ListingResultsView listings={offerListings} kind="bien" Card={ListBiens} mode={resultsView} onModeChange={setResultsView}>
            {visibleListings => <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visibleListings.map((bien) => (
                <ListBiens 
                  key={bien.id || bien.ID}
                  listing={bien} 
                />
              ))}
            </ul>}
            </ListingResultsView>
          </main>
          {hasMore && (
            <div className="flex justify-center items-center">
              <button 
                disabled={loadingMore} aria-busy={loadingMore} onClick={onFetchMoreListings}
                className="bg-white px-3 py-1.5 text-gray-700 border border-gray-300 mb-6 mt-6 hover:border-state-600 rounded transition duration-150 ease-in-out"
              >
                {t('see_plus')}
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-[#0b2738] mb-2 font-semibold text-center text-xl">  {t('details_vacation.no_listings')}</p>
      )}
    </div>
  );
}
